import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useAuth } from '~auth/useAuth';
import {
  EditIcon,
  LoadingIcon,
  MoreIcon,
  NoDisplayIcon,
  SyncIcon,
  TrashIcon,
} from '~components/ui/icons';
import { PowerScheduleDetail_PowerScheduleFragment } from './__generated__/PowerScheduleDetail.graphql';

type Props = {
  powerSchedule: PowerScheduleDetail_PowerScheduleFragment;
  isOverLiteLimit?: boolean;
  onRemoveDisplays: () => void;
  onSync: () => void;
  onDelete: () => void;
  onRename: () => void;
};

export function PowerScheduleDetailMoreActions({
  powerSchedule,
  isOverLiteLimit = false,
  onRemoveDisplays,
  onSync,
  onDelete,
  onRename,
}: Props) {
  const { verifyUserPermissions } = useAuth();
  const hasPowerScheduleUpdatePermission = verifyUserPermissions([Permission.PowerscheduleUpdate]);
  const hasPowerScheduleDeletePermission = verifyUserPermissions([Permission.PowerscheduleDelete]);
  const hasDisplayContentUpdatePermission = verifyUserPermissions([
    Permission.DisplayContentUpdate,
  ]);

  const hasDisplays = powerSchedule.allDisplays.length > 0;
  const hasOutOfSyncDisplays = powerSchedule.outOfSyncDisplays.length > 0;
  const isSyncing = powerSchedule.syncingDisplays.length > 0;
  const isRemoving = powerSchedule.removingDisplays.length > 0;
  const isUpdating = isSyncing || isRemoving;

  return (
    <Menu>
      <MenuButton as={Button} aria-label="Options" variant="outline">
        <MoreIcon />
      </MenuButton>
      <MenuList>
        <MenuItem
          icon={<EditIcon />}
          onClick={onRename}
          isDisabled={!hasPowerScheduleUpdatePermission || isOverLiteLimit}
        >
          {t('rename')}
        </MenuItem>
        <MenuItem
          icon={isSyncing ? <LoadingIcon /> : <SyncIcon />}
          isDisabled={!hasDisplayContentUpdatePermission || isUpdating || !hasOutOfSyncDisplays}
          onClick={onSync}
        >
          {t('sync')}
        </MenuItem>
        <MenuItem
          icon={isRemoving ? <LoadingIcon /> : <NoDisplayIcon />}
          isDisabled={!hasDisplayContentUpdatePermission || isUpdating || !hasDisplays}
          onClick={onRemoveDisplays}
        >
          {t('removeFromDisplays')}
        </MenuItem>
        <MenuItem
          icon={<TrashIcon color="red.500" />}
          color={'red.500'}
          isDisabled={!hasPowerScheduleDeletePermission || isUpdating}
          onClick={onDelete}
        >
          {t('delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
